import React, { useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'
import Toast from 'shared/ui/Toast/Toast'
import PdfPreviewPortal from 'pages/ApplicationDetailsPage/components/Checklist/components/PdfPreviewPortal/PdfPreviewPortal'
import { navigateToStudentProfile } from 'pages/Dashboard/components/TodaysPrioritySection/utils/priorityCardUi'
import { ReactComponent as TitleIcon } from 'assets/icons/dock_pen.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as EyeIcon } from 'assets/icons/openedEye.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { ReactComponent as StarsIcon } from 'assets/icons/stars.svg'
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg'
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg'
import Card from './components/Card/Card'
import Writing from './components/Writing/Writing'
import ResultDrawer from './components/ResultDrawer/ResultDrawer'
import { useGenerateLorModal } from './hooks/useGenerateLorModal'
import s from './styles/GenerateLorModal.module.scss'

const PILL_SKELETONS = [0, 1, 2, 3]
const LETTER_SKELETONS = [0, 1]

const GenerateLorModal = ({
	isOpen,
	onClose,
	application,
	onLetterSaved,
	initialLetter = null,
	onAssignBragsheet,
}) => {
	const navigate = useNavigate()
	const changeRef = useRef(null)
	const {
		selectedApplication,
		contextStatus,
		contextError,
		instructions,
		tones,
		highlights,
		tonesList,
		highlightsList,
		showAllHighlights,
		showAllReferences,
		useBragSheet,
		useProfileBuilder,
		selectedReferenceId,
		references,
		visibleReferences,
		bragSheet,
		profileBuilder,
		applications,
		isAppPopoverOpen,
		pdfPreview,
		isPreviewLoading,
		mode,
		letter,
		writingStatus,
		generationError,
		drawerMode,
		savedPillVisible,
		toast,
		canChangeApplication,
		isComposerOpen,
		isWritingOpen,
		isResultOpen,
		changeInstructions,
		toggleTone,
		toggleHighlight,
		toggleHighlightsVisibility,
		toggleReferencesVisibility,
		toggleBragSheet,
		toggleProfileBuilder,
		selectReference,
		stopPropagation,
		changeApplication,
		selectApplication,
		closeAppPopover,
		preview,
		closePdfPreview,
		generate,
		stopWriting,
		retryGeneration,
		editResult,
		improveResult,
		copyResult,
		downloadResult,
		saveLetterSnapshot,
		registerEditorFlush,
		setDrawerMode,
		closeToast,
		reloadContext,
	} = useGenerateLorModal({
		isOpen,
		onClose,
		application,
		onLetterSaved,
		initialLetter,
	})

	const visibleHighlights = showAllHighlights
		? highlightsList
		: highlightsList.slice(0, 8)
	const studentName = selectedApplication?.studentName || ''
	const studentId = selectedApplication?.studentId || ''
	const university = selectedApplication?.university || ''
	const program = selectedApplication?.program || ''
	const intake = selectedApplication?.intake || ''
	const canOpenStudent = Boolean(String(studentId || '').trim())
	const openStudentProfile = () => {
		navigateToStudentProfile(navigate, studentId)
	}
	const isLoading =
		contextStatus === 'loading' || contextStatus === 'idle'
	const showSections = isLoading || contextStatus === 'ready'

	if (!isOpen) return null

	return (
		<>
			{isResultOpen ? (
				<ResultDrawer
					application={selectedApplication}
					letter={letter}
					drawerMode={drawerMode}
					savedPillVisible={savedPillVisible}
					onClose={onClose}
					onEdit={editResult}
					onImprove={improveResult}
					onCopy={copyResult}
					onDownload={downloadResult}
					onSaveSnapshot={saveLetterSnapshot}
					registerEditorFlush={registerEditorFlush}
					setDrawerMode={setDrawerMode}
				/>
			) : null}

			{isWritingOpen
				? createPortal(
						<div
							className={s.overlay}
							role="dialog"
							aria-modal="true"
							aria-label="Generating Letter of Recommendation"
						>
							<div className={s.backdrop} onClick={stopWriting} />
							<div className={s.writing} onClick={stopPropagation}>
								<Writing
									status={writingStatus}
									errorMessage={generationError}
									onClose={stopWriting}
									onBack={stopWriting}
									onRetry={retryGeneration}
								/>
							</div>
						</div>,
						document.body,
					)
				: null}

			{isComposerOpen
				? createPortal(
						<div
							className={s.overlay}
							role="dialog"
							aria-modal="true"
							aria-labelledby="generate-lor-title"
						>
							<div className={s.backdrop} onClick={onClose} />
							<div className={s.modal} onClick={stopPropagation}>
								<div className={s.inner}>
									<header className={s.header}>
										<span className={s.header__icon}>
											<TitleIcon />
										</span>
										<div className={s.header__inner}>
											<h1 id="generate-lor-title" className={s.header__title}>
												{mode === 'improve'
													? 'Improve Letter of Recommendation'
													: 'Generate Letter of Recommendation'}
											</h1>
											<p className={s.header__subtitle}>
												AI will draft a personalized LoR using the student's
												background and your guidance.
											</p>
										</div>
										<button
											type="button"
											className={s.close}
											onClick={onClose}
											aria-label="Close"
										>
											<CrossIcon />
										</button>
									</header>

									<section className={s.student} ref={changeRef}>
										<span className={s.student__avatar}>
											<InitialAvatar
												name={studentName || 'Student'}
												size={44}
												studentId={studentId}
												onClick={
													canOpenStudent ? openStudentProfile : undefined
												}
											/>
										</span>
										<div className={s.student__inner}>
											{canOpenStudent ? (
												<button
													type="button"
													className={s.student__title}
													onClick={openStudentProfile}
												>
													{studentName || 'Student'}
												</button>
											) : (
												<h2 className={s.student__title}>
													{studentName || 'Student'}
												</h2>
											)}
											<p className={s.student__meta}>
												<span>{university || '—'}</span>
												<span aria-hidden>•</span>
												<span>{program || '—'}</span>
												{intake ? (
													<>
														<span aria-hidden>•</span>
														<span>{intake}</span>
													</>
												) : null}
											</p>
										</div>
										{canChangeApplication ? (
											<button
												type="button"
												className={s.student__change}
												onClick={changeApplication}
												aria-expanded={isAppPopoverOpen}
											>
												<PenIcon />
												Change application
											</button>
										) : null}

										{isAppPopoverOpen ? (
											<div className={s.popover} role="listbox">
												<div className={s.popover__list}>
													{applications.length ? (
														applications.map(item => {
															const isActive =
																item.applicationTargetId ===
																selectedApplication?.applicationTargetId
															return (
																<button
																	key={item.applicationTargetId}
																	type="button"
																	className={`${s.popover__item} ${isActive ? s.active : ''}`}
																	onClick={() => selectApplication(item)}
																>
																	<strong>{item.university || 'University'}</strong>
																	<span>{item.program || 'Program'}</span>
																</button>
															)
														})
													) : (
														<p className={s.popover__empty}>
															No applications available.
														</p>
													)}
												</div>
												<button
													type="button"
													className={s.popover__close}
													onClick={closeAppPopover}
												>
													Close
												</button>
											</div>
										) : null}
									</section>

									<div className={s.body}>
										{contextStatus === 'error' ? (
											<div className={s.state}>
												<p>{contextError}</p>
												<button
													type="button"
													className={s.more}
													onClick={reloadContext}
												>
													Retry
												</button>
											</div>
										) : null}

										{showSections ? (
											<>
												<section
													className={`${s.section} ${s.referencePanel}`}
												>
													<div className={s.groupHeading}>
														<span className={s.step}>1</span>
														<div>
															<h2 className={s.groupTitle}>
																Use a previous letter as reference
															</h2>
															<p className={s.hint}>
																{isLoading || references.length
																	? 'Choose a writing example for tone and structure. The original letter will not be modified.'
																	: 'This student does not have a previous letter to use as a reference yet.'}
															</p>
														</div>
														<span className={s.optional}>Optional</span>
													</div>
													{isLoading ? (
														<div className={s.letters}>
															{LETTER_SKELETONS.map(item => (
																<Card
																	key={`letter-skeleton-${item}`}
																	isLoading
																/>
															))}
														</div>
													) : references.length ? (
														<div className={s.letters}>
															{visibleReferences.map(item => (
																<Card
																	key={item.id}
																	{...item}
																	selected={selectedReferenceId === item.id}
																	onSelect={() => selectReference(item.id)}
																/>
															))}
															{references.length > 2 ? (
																<button
																	type="button"
																	className={s.all}
																	onClick={toggleReferencesVisibility}
																>
																	<PlusIcon />
																	<span className={s.all__inner}>
																		<strong>
																			{showAllReferences
																				? 'Hide letters'
																				: 'View all letters'}
																		</strong>
																		<small>
																			{showAllReferences
																				? 'Collapse previous LoRs'
																				: 'See all previous LoRs'}
																		</small>
																	</span>
																</button>
															) : null}
														</div>
													) : null}
												</section>

												<div className={s.guidancePanel}>
													<div className={s.groupHeading}>
														<span className={s.step}>2</span>
														<div>
															<h2 className={s.groupTitle}>Writing guidance</h2>
															<p className={s.hint}>
																Shape what the draft should say and how it should
																sound.
															</p>
														</div>
													</div>
													<section className={s.section}>
														<div className={s.section__head}>
															<label
																className={s.label}
																htmlFor="lor-instructions"
															>
																Additional instructions
															</label>
															{!isLoading ? (
																<span className={s.section__meta}>
																	<span className={s.optional}>Optional</span>
																	<span className={s.section__count}>
																		{instructions.length} / 1000
																	</span>
																</span>
															) : null}
														</div>
														<div
															className={`${s.textarea} ${isLoading ? s.loading : ''}`}
														>
															{isLoading ? (
																<LoaderImg />
															) : (
																<textarea
																	id="lor-instructions"
																	value={instructions}
																	onChange={changeInstructions}
																	maxLength={1000}
																	placeholder="Add any specific notes for the AI writer..."
																/>
															)}
														</div>
													</section>

													<div className={s.sections}>
														<section className={s.section}>
														<h2 className={s.label}>Writing tone</h2>
														<div className={s.pills}>
															{isLoading
																? PILL_SKELETONS.map(item => (
																		<span
																			key={`tone-skeleton-${item}`}
																			className={`${s.pill} ${s.loading}`}
																		>
																			<LoaderImg />
																		</span>
																	))
																: tonesList.map(item => {
																		const isActive = tones.includes(item)
																		return (
																			<button
																				key={item}
																				type="button"
																				className={`${s.pill} ${isActive ? s.active : ''}`}
																				onClick={() => toggleTone(item)}
																				aria-pressed={isActive}
																			>
																				{item}
																			</button>
																		)
																	})}
														</div>
													</section>

													<section className={s.section}>
														<h2 className={s.label}>Emphasize in the letter</h2>
														<div className={s.pills}>
															{isLoading
																? PILL_SKELETONS.map(item => (
																		<span
																			key={`highlight-skeleton-${item}`}
																			className={`${s.pill} ${s.loading}`}
																		>
																			<LoaderImg />
																		</span>
																	))
																: visibleHighlights.map(item => {
																		const isActive = highlights.includes(item)
																		return (
																			<button
																				key={item}
																				type="button"
																				className={`${s.pill} ${isActive ? s.active : ''}`}
																				onClick={() => toggleHighlight(item)}
																				aria-pressed={isActive}
																			>
																				{item}
																			</button>
																		)
																	})}
															{!isLoading && highlightsList.length > 8 ? (
																<button
																	type="button"
																	className={s.more}
																	onClick={toggleHighlightsVisibility}
																	aria-expanded={showAllHighlights}
																>
																	{showAllHighlights ? 'Hide' : 'View all'}
																</button>
															) : null}
														</div>
														</section>
													</div>
												</div>

												<section
													className={`${s.section} ${s.context} ${s.contextPanel}`}
												>
													<div className={s.groupHeading}>
														<span className={s.step}>3</span>
														<div>
															<h2 className={s.groupTitle}>Student context</h2>
															<p className={s.hint}>
																Choose the student information you want the AI
																to use.
															</p>
														</div>
													</div>

													{isLoading ? (
														<div className={`${s.context__row} ${s.loading}`}>
															<LoaderImg />
														</div>
													) : bragSheet.available ? (
														<div className={s.context__row}>
															<label className={s.toggle}>
																<input
																	type="checkbox"
																	checked={useBragSheet}
																	onChange={toggleBragSheet}
																/>
																<span className={s.toggle__track}>
																	<span className={s.toggle__thumb} />
																</span>
																<span className={s.toggle__label}>
																	<strong>Brag Sheet</strong>
																	<small>
																		Includes achievements, activities, personal
																		stories, and more.
																	</small>
																</span>
															</label>
															<button
																type="button"
																className={s.preview}
																onClick={preview}
																disabled={isPreviewLoading}
																aria-busy={isPreviewLoading}
															>
																{isPreviewLoading ? (
																	<span className={s.preview__spinner} aria-hidden />
																) : (
																	<>
																		<EyeIcon />
																		<span>Preview</span>
																	</>
																)}
															</button>
														</div>
													) : (
														<div
															className={`${s.context__grid} ${profileBuilder.usable ? s.split : ''}`}
														>
															{profileBuilder.usable ? (
																<label className={`${s.toggle} ${s.cardish}`}>
																	<input
																		type="checkbox"
																		checked={useProfileBuilder}
																		onChange={toggleProfileBuilder}
																	/>
																	<span className={s.toggle__track}>
																		<span className={s.toggle__thumb} />
																	</span>
																	<span className={s.toggle__label}>
																		<strong>Profile Builder</strong>
																		<small>
																			{profileBuilder.completionPercent}% complete
																		</small>
																		<span className={s.progress}>
																			<span
																				className={s.progress__bar}
																				style={{
																					width: `${profileBuilder.completionPercent}%`,
																				}}
																			/>
																		</span>
																	</span>
																</label>
															) : null}

															<div className={s.warning}>
																<div className={s.warning__inner}>
																	<div className={s.warning__content}>
																		<div className={s.warning__heading}>
																			<WarningIcon aria-hidden />
																			<strong>Student has no Brag Sheet</strong>
																		</div>
																		<p>
																			Assign a Brag Sheet to give the AI richer
																			personal context.
																		</p>
																	</div>
																	<button
																		type="button"
																		className={s.warning__action}
																		onClick={() => onAssignBragsheet?.()}
																	>
																		Assign Brag Sheet
																	</button>
																</div>
															</div>
														</div>
													)}
												</section>

											</>
										) : null}
									</div>
								</div>

								<footer className={s.footer}>
									<button type="button" className={s.cancel} onClick={onClose}>
										Cancel
									</button>
									<span className={s.secure}>
										<LockIcon />
										Your instructions and student data are private and secure.
									</span>
									<button
										type="button"
										className={s.generate}
										onClick={generate}
										disabled={contextStatus !== 'ready'}
									>
										<StarsIcon />
										{mode === 'improve' ? 'Improve LoR' : 'Generate LoR'}
									</button>
								</footer>
							</div>
						</div>,
						document.body,
					)
				: null}

			<PdfPreviewPortal
				open={pdfPreview.open}
				url={pdfPreview.url}
				title={pdfPreview.title}
				onClose={closePdfPreview}
				onOpenInNewTab={closePdfPreview}
			/>

			<Toast
				isVisible={toast.isVisible}
				message={toast.message}
				type={toast.type}
				onClose={closeToast}
			/>
		</>
	)
}

export default GenerateLorModal
